import Spinner from "components/atoms/loader/Spinner";
import { useCallback, useLayoutEffect, useRef } from "react";
import styled from "styled-components";

interface InfiniteTableProps {
  data: any[];
  loadMore: () => void;
  isLoading: boolean;
  hasMore: boolean;
  leftPadding?: number;
  initialScrollTop?: number;
  onScrollPosition?: (scrollTop: number) => void;
  height?: string;
  children: React.ReactNode;
}

export default function InfiniteWrapper(props: InfiniteTableProps) {
  const {
    data,
    loadMore,
    isLoading,
    hasMore,
    initialScrollTop = 0,
    onScrollPosition,
    height = "500px",
    children,
  } = props;

  const observerRef = useRef<HTMLDivElement>(null);
  const onLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      loadMore();
    }
  }, [isLoading, hasMore, loadMore]);

  useLayoutEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
          onLoadMore();
        }
      },
      {
        threshold: 0.1,
      }
    );

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }

    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [onLoadMore]);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const getSBW = useCallback(() => {
    if (isLoading) return;
    if (!scrollRef.current) return;
    if (scrollRef.current.getAttribute("data-sbw")) return;
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const sbw = el.offsetWidth - el.clientWidth; // ✅ 세로 스크롤바 폭
      el.style.setProperty("--sbw", `${sbw}px`);
    };

    update();
  }, [isLoading]);

  useLayoutEffect(() => {
    getSBW();
  }, [getSBW]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const top = e.currentTarget.scrollTop;
      onScrollPosition?.(top);
    },
    [onScrollPosition]
  );

  useLayoutEffect(() => {
    if (initialScrollTop <= 0 || !scrollRef.current) return;
    scrollRef.current.scrollTop = initialScrollTop;
  }, [initialScrollTop]);

  // 초기 로딩(데이터 없음)일 때만 전체 스피너. 다음 페이지 로드 시에는 테이블 유지해 스크롤 위치 보존
  if (isLoading && data.length === 0) {
    return (
      <LoadingContainer>
        <Spinner size={14} />
      </LoadingContainer>
    );
  }

  return (
    <InfiniteScroll
      ref={scrollRef}
      className="infinite-scroll"
      onScroll={handleScroll}
      height={height}
    >
      {children}
      {hasMore && !isLoading && (
        <LoadMoreTrigger ref={observerRef}>
          {/* {isLoading && */}
          <Spinner size={12} />
          {/*  } */}
        </LoadMoreTrigger>
      )}
    </InfiniteScroll>
  );
}

export const InfiniteScroll = styled.div<{ height?: string }>`
  max-height: ${({ height }) => height ?? "500px"};
  overflow-y: auto;
  box-sizing: border-box;
  width: 100%;
  overflow-x: hidden;
  .spinner-wrapper {
    padding: 0;
  }
  > div {
    width: calc(100% + var(--sbw, 0px));
    box-sizing: border-box;
    height: auto;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
`;

export const LoadMoreTrigger = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  width: 100%;

  .spinner-wrapper {
    position: static;
    padding-top: 0;
  }
`;
